<template>
  <DashboardCard :border="false" :firstContent="true" class="mt-0" :padding="false">
    <template v-slot:table>
      <div class="table-wrapper">
        <MDBTable borderless class="table-dashboard score-table width-unset">
          <thead>
            <tr>
              <th v-for="col in visibleColumns" :key="col.key" :aria-sort="ariaSortFor(col.key)"
                @click="isSortable(col.key) && toggleSort(col.key)"
                :class="['col', `col-${col.key}`, alignClass(col), columnClass(col)]" role="columnheader"
                :tabindex="isSortable(col.key) ? 0 : -1"
                @keydown.enter.prevent="isSortable(col.key) && toggleSort(col.key)"
                @keydown.space.prevent="isSortable(col.key) && toggleSort(col.key)">
                <span class="th-content">
                  {{ col.label }}
                  <span v-if="isSortable(col.key)" class="sort-indicator" aria-hidden="true">
                    <span class="chev up" :class="{ active: sort_by === col.key && sort_order === 'asc' }">▲</span>
                    <span class="chev down" :class="{ active: sort_by === col.key && sort_order === 'desc' }">▼</span>
                  </span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!rowsToDisplay.length && !loading">
              <td :colspan="visibleColumns.length" class="text-center py-4">No data</td>
            </tr>
            <tr v-for="(row, idx) in rowsToDisplay" :key="idx">
              <td v-for="col in visibleColumns" :key="col.key"
                :class="['col', `col-${col.key}`, alignClass(col), columnClass(col), isNegative(row, col) ? 'negative' : '']">
                {{ formatCell(row, col) }}
              </td>
            </tr>
            <tr v-if="loading">
              <td :colspan="visibleColumns.length" class="text-center py-4">
                <Loader :show="true" />
              </td>
            </tr>
          </tbody>
        </MDBTable>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper d-flex justify-content-between align-items-center mt-4">
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: meta.page === 1 }">
            <button class="page-link" @click="goToPage(meta.page - 1)" :disabled="meta.page === 1">
              <MDBIcon fas icon="angle-left" />
            </button>
          </li>

          <li v-for="p in pagesToShow" :key="p" class="page-item" :class="{ active: meta.page === p }">
            <button class="page-link" @click="goToPage(p)">{{ p }}</button>
          </li>

          <li class="page-item" :class="{ disabled: meta.page === meta.pages }">
            <button class="page-link" @click="goToPage(meta.page + 1)" :disabled="meta.page === meta.pages">
              <MDBIcon fas icon="angle-right" />
            </button>
          </li>
        </ul>

        <div class="dropdown-wrapper d-flex align-items-center gap-2">
          <select v-model="selectedPerPage" @change="changePerPage" class="dropdown">
            <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
          <span class="dropdown-info">
            {{ pageStart }} to {{ pageEnd }} of {{ totalRows }}
          </span>
        </div>
      </div>
    </template>
  </DashboardCard>
</template>

<script setup>
import Loader from "@/components/General/Loader/index.vue";
import { MDBTable, MDBIcon } from "mdb-vue-ui-kit";
import { ref, reactive, computed, defineProps, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { KpiDashboardService } from "@/services/kpi-dashboard.service";
import DashboardCard from "../User/Wholesaler/DashboardCard.vue";
import getSeasonLabel from "@/helpers/season";

const { t } = useI18n();
const SHOW_ALL_OPTION = "Show All";
const SHOW_ALL_PER_PAGE = 1000;
const props = defineProps({
  dealerRows: { type: Array, default: null },
  data: { type: Object, default: null },
  userId: { type: [String, Number], default: null },
});

const loading = ref(false);
const allRows = ref([]);
const sort_by = ref("date");
const sort_order = ref("desc");

const meta = reactive({
  total: 0,
  per_page: 20,
  page: 1,
  pages: 1,
});

const metadata = ref({
  show_all: true,
  show_download: true,
});

const selectedPerPage = ref(meta.per_page);

const perPageOptions = computed(() => {
  const baseOptions = [10, 20, 50, 100];
  if (metadata.value.show_all) {
    return [...baseOptions, SHOW_ALL_OPTION];
  }
  return baseOptions;
});

const isShowAllSelected = () => selectedPerPage.value === SHOW_ALL_OPTION;

const allColumns = [
  { label: t("widgets.dealerTable.columns.date"), key: "date", width: 9, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.wholesaler_bs_plus"), key: "wholesaler_bs_plus", width: 15, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.customer_number"), key: "customer_number", width: 9, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.article"), key: "article", width: 8, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.brand"), key: "brand", width: 7, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.product"), key: "product", width: 9, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.type"), key: "type", width: 8, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.inch"), key: "inch", width: 5, align: "center", sortable: true },
  { label: t("widgets.dealerTable.columns.season"), key: "season", width: 5, align: "center", sortable: true },
  { label: t("widgets.dealerTable.columns.amount"), key: "amount", width: 6, align: "right", numeric: true, highlightNegative: true, sortable: true },
  { label: t("widgets.dealerTable.columns.points"), key: "points", width: 6, align: "right", numeric: true, sortable: true },
  { label: t("widgets.dealerTable.columns.activity"), key: "activity", width: 7, align: "left", sortable: true },
  { label: t("widgets.dealerTable.columns.total_points"), key: "total_points", width: 6, align: "right", numeric: true, sortable: true },
];

const rowsToDisplay = computed(() => {
  if (allRows.value.length > 0) {
    return allRows.value;
  }
  return [];
});

const visibleColumns = computed(() => {
  if (!rowsToDisplay.value.length) {
    return allColumns;
  }
  const availableKeys = new Set();
  rowsToDisplay.value.forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row).forEach((key) => availableKeys.add(key));
    }
  });
  return allColumns.filter((col) => availableKeys.has(col.key));
});

const totalRows = computed(() => meta.total || allRows.value.length);
const pageStart = computed(() => (!rowsToDisplay.value.length ? 0 : (meta.page - 1) * meta.per_page + 1));
const pageEnd = computed(() => {
  if (!rowsToDisplay.value.length) return 0;
  if (selectedPerPage.value === SHOW_ALL_OPTION) {
    return totalRows.value;
  }
  return Math.min(meta.page * meta.per_page, totalRows.value);
});

const pagesToShow = computed(() => {
  const pages = [];
  const totalPages = meta.pages || 1;
  const current = meta.page;
  let start = Math.max(1, current - 2);
  let end = Math.min(totalPages, current + 2);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

function setRowsFromArray(rows) {
  allRows.value = Array.isArray(rows) ? [...rows] : [];
  meta.total = allRows.value.length;
  meta.page = 1;
  meta.per_page = meta.per_page || 20;
  meta.pages = meta.total ? Math.ceil(meta.total / meta.per_page) : 1;
}

function applyListPayload(payload) {
  const list = payload?.dealer_list ?? payload ?? {};
  const rows = Array.isArray(list.data) ? list.data : [];

  allRows.value = rows;
  meta.total = Number(list.total ?? rows.length);
  meta.per_page = Number(list.per_page ?? meta.per_page);
  meta.page = Number(list.page ?? meta.page);
  meta.pages = Number(
      list.pages ?? (Math.ceil(meta.total / meta.per_page) || 1)
  );

  if (list.sort_by) sort_by.value = list.sort_by;
  if (list.sort_order) sort_order.value = list.sort_order;

  if (list.metadata) {
    metadata.value = {
      show_all: list.metadata.show_all !== false,
      show_download: list.metadata.show_download !== false,
    };
  }

  if (!isShowAllSelected()) {
    selectedPerPage.value = meta.per_page;
  }
}

watch(
  () => props.dealerRows,
  (rows) => {
    if (!rows) return;
    setRowsFromArray(rows);
  },
  { immediate: true }
);

function isSortable(key) {
  return visibleColumns.value.find((c) => c.key === key)?.sortable ?? false;
}
function toggleSort(key) {
  if (sort_by.value === key) sort_order.value = sort_order.value === "asc" ? "desc" : "asc";
  else {
    sort_by.value = key;
    sort_order.value = "asc";
  }
  fetchPaginatedRows();
}
function ariaSortFor(key) {
  if (sort_by.value !== key) return "none";
  return sort_order.value === "asc" ? "ascending" : "descending";
}

function formatCell(row, col) {
  const v = row?.[col.key];
  if (v === null || v === undefined || v === "" || v === "in-progress") return "-";
  if (col.key === "season") return getSeasonLabel(v, t, "trainings.seasons");
  if (col.key === "activity") {
    const activityKey = `widgets.dealerTable.activities.${v}`;
    const translated = t(activityKey);
    if (translated !== activityKey) return translated;
  }
  if (col.key === "wholesaler_bs_plus" && typeof v === "string" && v.toLowerCase() === "bridgestone") {
    return "Bridgestone";
  }
  return v;
}
function isNegative(row, col) {
  if (!col.highlightNegative) return false;
  const v = row?.[col.key];
  return typeof v === "number" && v < 0;
}
function alignClass(col) {
  return col.align === "right" ? "align-right" : col.align === "center" ? "align-center" : "align-left";
}

const totalWidth = computed(() =>
  visibleColumns.value.reduce((sum, col) => sum + (col.width || 0), 0)
);
const columnClass = (col) => {
  if (col.width && totalWidth.value > 0) {
    const percent = Math.round((col.width / totalWidth.value) * 100);
    return `w-${percent}`;
  }
  return "";
};

function goToPage(page) {
  if (page < 1 || page > meta.pages) return;
  meta.page = page;
  fetchPaginatedRows();
}

function changePerPage() {
  if (isShowAllSelected()) {
    loadAllRows();
    return;
  }

  meta.per_page = Number(selectedPerPage.value);
  meta.page = 1;
  fetchPaginatedRows();
}

async function fetchListPage(page) {
  const params = {
    page,
    per_page: SHOW_ALL_PER_PAGE,
    sort_by: sort_by.value,
    sort_order: sort_order.value,
  };

  return props.userId
    ? await KpiDashboardService.fetchCustomerDetails(props.userId, params)
    : await KpiDashboardService.fetchCustomers(params);
}

function extractListPayload(resp) {
  return resp?.dealer_list ?? resp?.wholesaler_list ?? resp ?? {};
}

async function loadAllRows() {
  if (loading.value) return;

  loading.value = true;

  try {
    const firstResp = await fetchListPage(1);
    const firstList = extractListPayload(firstResp);
    const rows = Array.isArray(firstList.data) ? [...firstList.data] : [];
    const total = Number(firstList.total ?? rows.length);
    const totalPages = Number(
      firstList.pages ?? (Math.ceil(total / SHOW_ALL_PER_PAGE) || 1),
    );

    for (let page = 2; page <= totalPages; page++) {
      const nextResp = await fetchListPage(page);
      const nextList = extractListPayload(nextResp);
      if (Array.isArray(nextList.data)) {
        rows.push(...nextList.data);
      }
    }

    allRows.value = rows;
    meta.total = total;
    meta.per_page = total;
    meta.page = 1;
    meta.pages = 1;

    if (firstList.sort_by) sort_by.value = firstList.sort_by;
    if (firstList.sort_order) sort_order.value = firstList.sort_order;

    if (firstList.metadata) {
      metadata.value = {
        show_all: firstList.metadata.show_all !== false,
        show_download: firstList.metadata.show_download !== false,
      };
    }
  } catch (err) {
    console.error("Show all fetch failed:", err);
    allRows.value = [];
    meta.total = 0;
    meta.pages = 1;
  } finally {
    loading.value = false;
  }
}

async function fetchPaginatedRows() {
  if (isShowAllSelected()) {
    await loadAllRows();
    return;
  }

  loading.value = true;

  try {
    const params = {
      page: meta.page,
      per_page: meta.per_page,
      sort_by: sort_by.value,
      sort_order: sort_order.value,
    };

    let resp;

    if (props.userId) {
      resp = await KpiDashboardService.fetchCustomerDetails(props.userId, params);
      applyListPayload(resp);
    } else {
      resp = await KpiDashboardService.fetchCustomers(params);
      applyListPayload(resp);
    }
  } catch (err) {
    console.error("Pagination fetch failed:", err);
    allRows.value = [];
    meta.total = 0;
    meta.pages = 1;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (props.dealerRows) {
    setRowsFromArray(props.dealerRows);
    return;
  }
  if (props.data) {
    applyListPayload(props.data);

    loading.value = false;
    if (allRows.value.length) {
      return;
    }
  }

  fetchPaginatedRows();
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/table_dashboard.scss";

.sort-indicator .chev {
  opacity: 0.3;
  font-size: 10px;
  line-height: 1;
}

.sort-indicator .chev.active {
  opacity: 1;
}

.col {
  padding: 8px 12px;
}

.th-content:hover {
  cursor: pointer;
}

.pagination-wrapper {
  gap: 10px;
}

.dropdown-wrapper select {
  min-width: 80px;
}
</style>
